import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

/**
 * Chat Drawer Controller
 *
 * Gère le drawer de chat accessible depuis le header.
 * Permet de voir les conversations, envoyer des messages et créer de nouvelles conversations.
 *
 * RGAA 4.1 compliant avec focus trap implementation.
 */
export default class extends Controller {
  static targets = [
    "button",
    "drawer",
    "badge",
    "badgeCount",
    "chatIcon",
    "roomList",
    "conversation",
    "messageList",
    "messageForm",
    "input",
    "sendButton",
    "loading",
    "empty",
    "overlay",
    "backButton",
    "title",
    "footer",
    "newChatButton",
    "newChatView",
    "roomItem"
  ]

  static values = {
    roomsUrl: String,
    unreadUrl: String,
    messagesUrlTemplate: String,
    createMessageUrlTemplate: String,
    chatIndexUrl: { type: String, default: "/chat_rooms" },
    pollingInterval: { type: Number, default: 30000 },
    currentRoomId: Number,
    isOpen: { type: Boolean, default: false },
    mobileBreakpoint: { type: Number, default: 768 }
  }

  static STORAGE_KEYS = {
    isOpen: "chatDrawerOpen",
    currentRoomId: "chatDrawerRoomId",
    scrollPosition: "chatDrawerScroll"
  }

  connect() {
    this.fetchUnreadCount()
    this.startPolling()
    this.bindKeyboardNavigation()
    this.bindGlobalShortcut()
    this.subscribeToUpdates()
    this.previouslyFocusedElement = null

    // Restaurer l'état depuis sessionStorage (Phase 2)
    this.restoreState()
  }

  disconnect() {
    this.stopPolling()
    this.unsubscribeFromUpdates()
    this.unbindGlobalShortcut()
    this.unbindKeyboardNavigation()
    // Symétrie complète (review PR #56) : le trap focus est posé sur le drawer
    // à l'ouverture — le retirer aussi si la navigation survient drawer ouvert.
    this.releaseFocusTrap()
  }

  // === Actions publiques ===

  toggle(event) {
    event.preventDefault()
    this.isOpenValue ? this.close() : this.open()
  }

  open() {
    this.previouslyFocusedElement = document.activeElement
    this.isOpenValue = true
    this.drawerTarget.classList.remove("hidden")
    this.buttonTarget.setAttribute("aria-expanded", "true")

    // Mobile: afficher overlay
    if (this.isMobile() && this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("hidden")
    }

    this.fetchRooms()
    this.setupFocusTrap()
    this.focusFirstElement()

    // Sauvegarder l'état (Phase 2)
    this.saveState()
  }

  close() {
    // Sauvegarder la position de scroll avant fermeture
    this.saveScrollPosition()

    this.isOpenValue = false
    this.drawerTarget.classList.add("hidden")
    this.buttonTarget.setAttribute("aria-expanded", "false")

    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("hidden")
    }

    // Réinitialiser la vue (seulement sur mobile)
    if (this.isMobile()) {
      this.showRoomList()
      this.currentRoomIdValue = 0
    }

    // Restaurer le focus
    this.releaseFocusTrap()
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus()
      this.previouslyFocusedElement = null
    }

    // Sauvegarder l'état fermé (Phase 2)
    this.saveState()
  }

  // Ouvre le drawer directement sur une room précise (appelé via data-action)
  openRoom(event) {
    event.preventDefault()
    const roomId = parseInt(event.currentTarget.dataset.chatDrawerRoomIdParam || event.currentTarget.dataset.roomId, 10)
    if (!roomId) return

    if (!this.isOpenValue) {
      this.open()
    }
    this.currentRoomIdValue = roomId
    this.fetchMessages(roomId)
    this.showConversation()

    if (this.isMobile() && this.hasBackButtonTarget) {
      this.backButtonTarget.classList.remove("hidden")
    }
    this.saveState()
  }

  selectRoom(event) {
    event.preventDefault()
    const roomId = parseInt(event.currentTarget.dataset.roomId, 10)
    if (!roomId) return

    this.currentRoomIdValue = roomId
    this.highlightSelectedRoom(roomId)
    this.fetchMessages(roomId)
    this.showConversation()

    // Mobile: afficher bouton retour
    if (this.isMobile() && this.hasBackButtonTarget) {
      this.backButtonTarget.classList.remove("hidden")
    }

    // Sauvegarder l'état avec la room sélectionnée
    this.saveState()
  }

  backToList(event) {
    event.preventDefault()
    this.currentRoomIdValue = 0
    this.showRoomList()

    if (this.hasBackButtonTarget) {
      this.backButtonTarget.classList.add("hidden")
    }

    // Rafraîchir la liste
    this.fetchRooms()
  }

  async sendMessage(event) {
    event.preventDefault()

    if (!this.currentRoomIdValue || !this.hasInputTarget) return

    const content = this.inputTarget.value.trim()
    if (!content) return

    // Désactiver le bouton pendant l'envoi
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = true
    }

    try {
      await this.postMessage(content)
      this.inputTarget.value = ""
      this.inputTarget.focus()
    } catch (error) {
      console.error("Erreur lors de l'envoi du message:", error)
    } finally {
      if (this.hasSendButtonTarget) {
        this.sendButtonTarget.disabled = false
      }
    }
  }

  handleInputKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.sendMessage(event)
    }
  }

  showNewChatForm(event) {
    event?.preventDefault()
    // Rediriger vers la page de chat (contexte-aware via Stimulus value)
    window.location.href = this.chatIndexUrlValue
  }

  // === Méthodes de fetch ===

  async fetchRooms() {
    if (!this.hasRoomsUrlValue) return

    this.showLoading()

    try {
      const response = await fetch(this.roomsUrlValue, {
        headers: { "Accept": "text/html" }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const html = await response.text()
      this.updateRoomList(html)
    } catch (error) {
      console.error("Erreur lors du chargement des conversations:", error)
      this.showError()
    } finally {
      this.hideLoading()
    }
  }

  async fetchMessages(roomId) {
    if (!this.hasMessagesUrlTemplateValue) return

    const url = this.messagesUrlTemplateValue.replace(":id", roomId)

    try {
      const response = await fetch(url, {
        headers: { "Accept": "text/html" }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const html = await response.text()
      this.updateMessageList(html)
      this.scrollToBottom()
    } catch (error) {
      console.error("Erreur lors du chargement des messages:", error)
    }
  }

  async fetchUnreadCount() {
    if (!this.hasUnreadUrlValue) return

    try {
      const response = await fetch(this.unreadUrlValue, {
        headers: { "Accept": "application/json" }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      this.updateBadge(data.unread_count)
    } catch (error) {
      console.error("Erreur lors du chargement des messages non lus:", error)
    }
  }

  async postMessage(content) {
    if (!this.hasCreateMessageUrlTemplateValue || !this.currentRoomIdValue) return

    const url = this.createMessageUrlTemplateValue.replace(":id", this.currentRoomIdValue)

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.getCSRFToken()
      },
      body: JSON.stringify({ message: { content } })
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    // Rafraîchir les messages
    await this.fetchMessages(this.currentRoomIdValue)
  }

  // === ActionCable ===

  subscribeToUpdates() {
    this.channel = consumer.subscriptions.create("ChatDrawerChannel", {
      connected: () => {
        console.log("Connected to ChatDrawerChannel")
      },
      disconnected: () => {
        console.log("Disconnected from ChatDrawerChannel")
      },
      received: (data) => {
        this.handleRealtimeUpdate(data)
      }
    })
  }

  unsubscribeFromUpdates() {
    if (this.channel) {
      this.channel.unsubscribe()
    }
  }

  handleRealtimeUpdate(data) {
    const { type, room_id, unread_count } = data

    // Mettre à jour le badge
    if (unread_count !== undefined) {
      this.updateBadge(unread_count)
    }

    // Si un nouveau message arrive dans la room ouverte, rafraîchir
    if (type === "new_message" && this.currentRoomIdValue === room_id) {
      this.fetchMessages(room_id)
    }

    // Rafraîchir la liste des rooms si le drawer est ouvert
    if (this.isOpenValue) {
      this.fetchRooms()
    }
  }

  // === UI Updates ===

  updateRoomList(html) {
    if (!this.hasRoomListTarget) return

    this.roomListTarget.innerHTML = html

    // Vérifier si la liste est vide
    const hasRooms = this.roomListTarget.querySelector("[data-room-id]")
    if (hasRooms) {
      this.hideEmpty()
    } else {
      this.showEmpty()
    }
  }

  updateMessageList(html) {
    if (!this.hasMessageListTarget) return
    this.messageListTarget.innerHTML = html
  }

  updateBadge(count) {
    if (!this.hasBadgeTarget) return

    if (count > 0) {
      this.badgeTarget.classList.remove("hidden")
      if (this.hasBadgeCountTarget) {
        this.badgeCountTarget.textContent = count > 99 ? "99+" : count
      }
      if (this.hasChatIconTarget) {
        this.chatIconTarget.classList.add("text-blue-600")
        this.chatIconTarget.classList.remove("text-gray-700")
      }
    } else {
      this.badgeTarget.classList.add("hidden")
      if (this.hasChatIconTarget) {
        this.chatIconTarget.classList.remove("text-blue-600")
        this.chatIconTarget.classList.add("text-gray-700")
      }
    }
  }

  highlightSelectedRoom(roomId) {
    // Retirer la surbrillance de tous les items
    if (this.hasRoomItemTarget) {
      this.roomItemTargets.forEach(item => {
        item.classList.remove("bg-blue-100")
      })

      // Ajouter la surbrillance à l'item sélectionné
      const selectedItem = this.roomItemTargets.find(
        item => parseInt(item.dataset.roomId, 10) === roomId
      )
      if (selectedItem) {
        selectedItem.classList.add("bg-blue-100")
      }
    }
  }

  showRoomList() {
    if (this.hasRoomListTarget) {
      this.roomListTarget.classList.remove("hidden")
    }
    if (this.hasConversationTarget) {
      this.conversationTarget.classList.add("hidden")
    }
    if (this.hasNewChatViewTarget) {
      this.newChatViewTarget.classList.add("hidden")
    }
    if (this.hasTitleTarget) {
      this.titleTarget.textContent = "Messages"
    }
    if (this.hasFooterTarget) {
      this.footerTarget.classList.remove("hidden")
    }
  }

  showConversation() {
    if (this.hasRoomListTarget) {
      this.roomListTarget.classList.add("hidden")
    }
    if (this.hasConversationTarget) {
      this.conversationTarget.classList.remove("hidden")
    }
    if (this.hasNewChatViewTarget) {
      this.newChatViewTarget.classList.add("hidden")
    }
    if (this.hasFooterTarget) {
      this.footerTarget.classList.add("hidden")
    }

    // Focus sur l'input
    setTimeout(() => {
      if (this.hasInputTarget) {
        this.inputTarget.focus()
      }
    }, 100)
  }

  scrollToBottom() {
    if (this.hasMessageListTarget) {
      this.messageListTarget.scrollTop = this.messageListTarget.scrollHeight
    }
  }

  // === Loading/Empty states ===

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
    }
    if (this.hasRoomListTarget) {
      this.roomListTarget.classList.add("hidden")
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
    }
    if (this.hasRoomListTarget) {
      this.roomListTarget.classList.remove("hidden")
    }
  }

  showEmpty() {
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.remove("hidden")
    }
    if (this.hasRoomListTarget) {
      this.roomListTarget.classList.add("hidden")
    }
  }

  hideEmpty() {
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.add("hidden")
    }
  }

  showError() {
    if (this.hasRoomListTarget) {
      this.roomListTarget.innerHTML = `
        <div class="flex flex-col items-center justify-center py-8 px-4">
          <i class="fas fa-exclamation-triangle text-3xl text-red-500 mb-3"></i>
          <p class="text-sm text-gray-600 text-center">Erreur lors du chargement</p>
        </div>
      `
    }
  }

  // === Polling ===

  startPolling() {
    if (this.pollingIntervalValue > 0) {
      this.pollingTimer = setInterval(() => {
        this.fetchUnreadCount()
        // Si le drawer est ouvert et qu'on est sur une conversation, rafraîchir les messages
        if (this.isOpenValue && this.currentRoomIdValue) {
          this.fetchMessages(this.currentRoomIdValue)
        }
      }, this.pollingIntervalValue)
    }
  }

  stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
    }
  }

  // === Focus Trap (RGAA 4.1) ===

  setupFocusTrap() {
    this.trapFocusHandler = this.trapFocus.bind(this)
    this.drawerTarget.addEventListener("keydown", this.trapFocusHandler)
  }

  releaseFocusTrap() {
    if (this.trapFocusHandler) {
      this.drawerTarget.removeEventListener("keydown", this.trapFocusHandler)
    }
  }

  trapFocus(event) {
    if (event.key !== "Tab") return

    const focusableElements = this.getFocusableElements()
    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
    }
  }

  getFocusableElements() {
    const selectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([type="hidden"]):not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ')

    return Array.from(this.drawerTarget.querySelectorAll(selectors)).filter(el => {
      return el.offsetWidth > 0 &&
             el.offsetHeight > 0 &&
             !el.hasAttribute('hidden') &&
             window.getComputedStyle(el).visibility !== 'hidden'
    })
  }

  focusFirstElement() {
    const focusableElements = this.getFocusableElements()
    if (focusableElements.length > 0) {
      setTimeout(() => focusableElements[0].focus(), 100)
    }
  }

  // === Keyboard Navigation ===

  bindKeyboardNavigation() {
    this.keydownHandler = (event) => {
      if (event.key === "Escape" && this.isOpenValue) {
        this.close()
      }
    }
    document.addEventListener("keydown", this.keydownHandler)
  }

  // Miroir obligatoire de bindKeyboardNavigation (audit 2026-08-04, A5-F1) :
  // le controller vit sur <body>, remplacé à CHAQUE navigation Turbo — sans
  // cleanup, un listener keydown s'empilait sur document par navigation.
  unbindKeyboardNavigation() {
    if (this.keydownHandler) {
      document.removeEventListener("keydown", this.keydownHandler)
    }
  }

  // === Global Shortcut (Phase 1) ===

  bindGlobalShortcut() {
    this.globalShortcutHandler = (event) => {
      // Ctrl+/ (Windows/Linux) ou Cmd+/ (Mac)
      const isShortcut = (event.ctrlKey || event.metaKey) && event.key === "/"

      if (isShortcut) {
        event.preventDefault()
        this.toggle(event)
      }
    }
    document.addEventListener("keydown", this.globalShortcutHandler)
  }

  unbindGlobalShortcut() {
    if (this.globalShortcutHandler) {
      document.removeEventListener("keydown", this.globalShortcutHandler)
    }
  }

  // === State Persistence (Phase 2) ===

  saveState() {
    const { isOpen, currentRoomId, scrollPosition } = this.constructor.STORAGE_KEYS

    // Ne persister que sur desktop
    if (this.isMobile()) {
      sessionStorage.removeItem(isOpen)
      sessionStorage.removeItem(currentRoomId)
      return
    }

    sessionStorage.setItem(isOpen, this.isOpenValue.toString())
    sessionStorage.setItem(currentRoomId, this.currentRoomIdValue.toString())
  }

  restoreState() {
    const { isOpen, currentRoomId, scrollPosition } = this.constructor.STORAGE_KEYS

    // Ne pas restaurer sur mobile
    if (this.isMobile()) return

    const wasOpen = sessionStorage.getItem(isOpen) === "true"
    const savedRoomId = parseInt(sessionStorage.getItem(currentRoomId) || "0", 10)
    const savedScroll = parseInt(sessionStorage.getItem(scrollPosition) || "0", 10)

    if (wasOpen) {
      // Ouvrir le drawer sans sauvegarder (évite boucle)
      this.isOpenValue = true
      this.drawerTarget.classList.remove("hidden")
      this.buttonTarget.setAttribute("aria-expanded", "true")

      this.fetchRooms().then(() => {
        // Si une conversation était ouverte, la restaurer
        if (savedRoomId > 0) {
          this.currentRoomIdValue = savedRoomId
          this.highlightSelectedRoom(savedRoomId)
          this.fetchMessages(savedRoomId).then(() => {
            this.showConversation()
            // Restaurer la position de scroll
            if (savedScroll > 0 && this.hasMessageListTarget) {
              this.messageListTarget.scrollTop = savedScroll
            }
          })
        }
      })
    }
  }

  saveScrollPosition() {
    if (this.hasMessageListTarget && this.currentRoomIdValue) {
      const { scrollPosition } = this.constructor.STORAGE_KEYS
      sessionStorage.setItem(scrollPosition, this.messageListTarget.scrollTop.toString())
    }
  }

  // === Utilities ===

  isMobile() {
    return window.innerWidth < this.mobileBreakpointValue
  }

  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
